/**
 * API Types for Workflow Agent
 *
 * These types represent the data structures used in backend API communication.
 * They should match the backend API response/request format exactly.
 *
 * Philosophy: Trust backend data. If backend sends malformed data,
 * let it fail fast rather than silently degrade.
 */

/**
 * Metadata for a node's position and dimensions.
 * Stored in agent_metadata to restore UI state on reload.
 */
export type NodeMetadata = {
  id: string;
  position: { x: number; y: number };
  measured?: { width?: number; height?: number };
  width?: number;
  height?: number;
};

/**
 * Metadata for an edge's connection points.
 * Stored in agent_metadata to restore edge handles on reload.
 */
export type EdgeMetadata = {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
};

/**
 * Complete metadata stored in WorkflowAgent.agent_metadata.
 * Contains UI state that needs to persist across sessions.
 */
export type WorkflowMetadata = {
  nodes: NodeMetadata[];
  edges: EdgeMetadata[];
};

/**
 * HATEOAS links for trigger resources.
 * Used for trigger CRUD operations.
 */
export type TriggerLinks = {
  self: string;
  collection: string;
  workflow_agent: string;
  update: string;
  delete: string;
  management: string;
};

/**
 * Node data that may have HATEOAS links attached
 */
export type NodeDataWithLinks = {
  links?: { self?: string };
};

/**
 * Extract the self URL from node data links.
 * Returns null if links or self URL is not present.
 *
 * Accepts Record<string, unknown> for compatibility with existing code,
 * but expects data to have a links.self structure if present.
 */
export function getTriggerSelfUrl(
  nodeData: NodeDataWithLinks | Record<string, unknown> | undefined
): string | null {
  if (!nodeData) return null;
  const links = (nodeData as NodeDataWithLinks).links;
  return links?.self || null;
}
